import moment from 'moment/moment'

import { TagProps } from '../components/app/design/Tag'
import { SelectOption } from '../components/settings/Options'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { mergeFk } from '../utils'
import {
    PREMIUM_ANNUAL_ID,
    PREMIUM_ANNUAL_PRICE,
    PREMIUM_ID,
    PREMIUM_PLUS_ANNUAL_ID,
    PREMIUM_PLUS_ANNUAL_PRICE,
    PREMIUM_PLUS_ID,
    PREMIUM_PLUS_PRICE,
    PREMIUM_PRICE,
    PREMIUM_SUITE_ANNUAL_ID,
    PREMIUM_SUITE_ANNUAL_PRICE,
    PREMIUM_SUITE_ID,
    PREMIUM_SUITE_PRICE,
} from '../utils/constants'
import { SHORT_DATE_FORMAT, dateByMovingToEndOfDay, dateFormat, isPast } from '../utils/dates'
import { currencyFloat, currencyFormat } from '../utils/numbers'
import { BaseRecord } from './BaseRecord'
import { Edition } from './Edition'
import { Gift } from './Gift'
import { Item } from './Item'
import { Option } from './Option'
import { Payment } from './Payment'
import { Promotion } from './Promotion'
import { FKType } from './SQLBuilder'
import { Source } from './Source'
import { Transactable } from './Transactable'
import { Transaction } from './Transaction'

type SubscriptionConstructorArgs = ConstructorArgs<() => Subscription>

export class Subscription extends Transaction implements Transactable {
    readonly recordType = 'subscription'

    readonly iTransactable = true

    memo?: string
    cancellationType?: string
    cancellationMemo?: string
    deposit?: number
    quantity?: number // Integer
    discountInstances?: number // Integer
    allowSurcharge?: boolean
    trialPeriod?: number // Integer
    reminderInstances?: number // Integer

    frequency?: string
    interval?: number // Integer
    instances?: number // Integer
    nextInvoiceDate?: moment.Moment

    confirmedDate?: moment.Moment
    completedDate?: moment.Moment
    paidDepositDate?: moment.Moment

    readonly paidInstances: number = 0
    readonly sourceDisplay?: string

    static SUBSCRIPTION_INSTANCES = 52
    static MINIMUM_NUMBER_OF_INSTANCES = 1
    static SUBSCRIPTION_TOTAL_LIMIT = 10000

    constructor(data?: Partial<SubscriptionConstructorArgs>, params?: { plan?: Item }) {
        super(data)
        if (!data) {
            this.date = moment().startOf('day')
            this.quantity = 1
            if (params?.plan) {
                this.plan = params.plan
                this.total = params.plan.rate
                this.instances = params.plan.instances
                this.interval = params.plan.interval
                this.frequency = params.plan.frequency
            }
            return
        }

        this.memo = data.memo ?? undefined
        this.cancellationType = data.cancellation_type ?? undefined
        this.cancellationMemo = data.cancellation_memo ?? undefined
        this.deposit = this.propertyFromCurrencyArg(data.deposit)
        this.quantity = this.propertyFromNumberArg(data.quantity)
        this.allowSurcharge = data.allow_surcharge ?? false
        this.frequency = data.frequency ?? undefined
        this.interval = this.propertyFromNumberArg(data.interval)
        this.instances = this.propertyFromNumberArg(data.instances)
        this.discountInstances = this.propertyFromNumberArg(data.discount_instances)
        this.trialPeriod = this.propertyFromNumberArg(data.trial_period)
        this.confirmedDate = this.propertyFromTimestampArg(data.confirmed_date)
        this.completedDate = this.propertyFromTimestampArg(data.completed_date)
        this.nextInvoiceDate = this.propertyFromDateArg(data.next_invoice_date)
        this.paidDepositDate = this.propertyFromTimestampArg(data.paid_deposit_date)
        this.reminderInstances = this.propertyFromNumberArg(data.reminder_instances)
        this.date = this.propertyFromDateArg(data.date)

        this.paidInstances = this.propertyFromNumberArg(data.paid_instances) ?? 0
        this.sourceDisplay = data.source_display ?? undefined
    }

    clone(overrides?: Partial<SubscriptionConstructorArgs>): Subscription {
        return new Subscription({ ...this.serializeForClone(), ...overrides })
    }

    get discountAmount(): number | undefined {
        if (this.discount && this.discount > 0) {
            let discount = this.discount
            if (this.discountType === 'percent' && this.total !== undefined)
                discount = currencyFloat((this.discount * (this.total ?? 0)) / 100)
            return discount
        }
        return undefined
    }

    get gratuityAmount(): number | undefined {
        if (this.gratuity && this.gratuity !== 0) {
            let gratuity = this.gratuity
            if (this.gratuityType === 'percent' && this.amount !== undefined)
                gratuity = currencyFloat((this.gratuity * this.amount) / 100)
            return gratuity
        }
        return undefined
    }

    get amount(): number | undefined {
        if (this.discount && this.discount > 0 && this.total !== undefined)
            return this.total - (this.discountAmount || 0)
        return this.total
    }

    get surchargeAmount(): number | undefined {
        if (this.allowSurcharge)
            return (
                ((this.amount || 0) + (this.gratuityAmount || 0) + (this.taxAmount || 0)) *
                Payment.PAYMENT_SURCHARGE_RATE
            )
        return undefined
    }

    get taxAmount(): number | undefined {
        if (this.tax && this.tax > 0 && this.amount !== undefined)
            return currencyFloat((this.tax * this.amount) / 100)
        return undefined
    }

    get chargeAmount(): number | undefined {
        return (this.amount || 0) + (this.taxAmount || 0) + (this.surchargeAmount || 0)
    }

    get depositChargeAmount(): number | undefined {
        let amount = this.deposit
        if (amount && amount > 0) {
            let taxAmount = this.tax && this.tax > 0 ? currencyFloat((this.tax * amount) / 100) : 0
            let surchargeAmount = this.allowSurcharge
                ? (taxAmount + amount || 0) * Payment.PAYMENT_SURCHARGE_RATE
                : 0
            return amount + taxAmount + surchargeAmount
        }
        return 0
    }

    get askingAmount(): number | undefined {
        return (this.amount || 0) + (this.taxAmount || 0)
    }

    get paidAmount(): number | undefined {
        return undefined
    }

    get owedAmount(): number | undefined {
        return this.amount
    }

    private get payoutDate(): moment.Moment | undefined {
        let date = this.nextInvoiceDate
        if (date) {
            date = moment(date).set({ hour: 9, minute: 30, second: 0 })
            if (date.isBefore(moment())) date = date.add(1, 'days')
        }
        return date
    }

    get isLate(): boolean {
        if (this.instances === undefined || this.instances === 0)
            return this.confirmedDate !== undefined

        let hours = this.vendor?.cancellationDuration ?? 0
        let start = this.date
        // todo: fix typescript error
        let lateDate = start?.subtract(hours, 'hours')
        return moment().isAfter(lateDate)
    }

    get shouldShowEnforceCheckboxForCancellation(): boolean {
        return this.cancellationAmount > 1.0 && this.isLate
    }

    get cancellationAmount(): number {
        let vendor = this.vendor
        if (
            vendor?.subscriptionCancellationRateType === 'rate' &&
            vendor?.subscriptionCancellationRate !== undefined
        ) {
            return vendor.subscriptionCancellationRate
        }
        else if (
            vendor?.subscriptionCancellationRateType === 'percent' &&
            vendor?.subscriptionCancellationRate !== undefined
        ) {
            let instances = this.instances !== undefined && this.instances > 0 ? this.instances : 1
            let total = this.total ?? 0
            return vendor.subscriptionCancellationRate * total * instances
        }
        return 0
    }

    get sourceString(): string | undefined {
        return this.id ? this.sourceDisplay : undefined
    }

    get statusString(): string {
        if (this.cancelledDate) {
            return `Canceled ${this.cancelledDate.format('MMM D, YYYY')}`
        }
        if (this.completedDate) {
            return `Completed ${this.completedDate.format('MMM D, YYYY')}`
        }
        if (this.confirmedDate && this.nextInvoiceDate?.startOf('day').isBefore(moment())) {
            return `Next retry ${this.payoutDate!.format('MMM D, YYYY')}`
        }
        if (this.confirmedDate) {
            return `Next payment ${this.nextInvoiceDate!.format('MMM D, YYYY')}`
        }
        if (this.id) {
            return `Sent ${this.createdDate!.format('MMM D')} - not paid`
        }
        return 'Requires payment'
    }

    get status(): { description: string; date: moment.Moment } | null {
        if (this.cancelledDate) {
            return { description: 'Canceled', date: this.cancelledDate }
        }
        if (this.completedDate) {
            return { description: 'Completed', date: this.completedDate }
        }
        if (this.confirmedDate && this.nextInvoiceDate?.startOf('day').isBefore(moment())) {
            return { description: 'Next retry', date: this.payoutDate! }
        }
        if (this.confirmedDate) {
            return { description: 'Next payment', date: this.nextInvoiceDate! }
        }
        if (this.id) {
            return { description: 'Sent - not paid', date: this.createdDate! }
        }
        return null
    }

    get statuses(): {
        title: string
        date: moment.Moment | undefined
        intent: TagProps['intent']
    }[] {
        return [
            {
                title: 'Sent',
                date: this.sentDate,
                intent: 'info',
            },
            {
                title: 'Viewed',
                date: this.viewedDate,
                intent: 'warning',
            },
            {
                title: 'Confirmed',
                date: this.confirmedDate,
                intent: 'success',
            },
        ]
    }

    get statusTag(): { label: string; intent: TagProps['intent'] } | null {
        const lastActiveStatus = this.statuses.findLast((status) => status.date !== undefined)
        if (!lastActiveStatus) {
            return null
        }

        return { label: lastActiveStatus.title, intent: lastActiveStatus.intent }
    }

    static frequencyStrings(): Option[] {
        return [
            new Option(undefined, { id: '1w', label: 'Every week' }),
            new Option(undefined, { id: '2w', label: 'Every 2 weeks' }),
            new Option(undefined, { id: '3w', label: 'Every 3 weeks' }),
            new Option(undefined, { id: '4w', label: 'Every 4 weeks' }),
            new Option(undefined, { id: '1m', label: 'Every month' }),
            new Option(undefined, { id: '5w', label: 'Every 5 weeks' }),
            new Option(undefined, { id: '6w', label: 'Every 6 weeks' }),
            new Option(undefined, { id: '7w', label: 'Every 7 weeks' }),
            new Option(undefined, { id: '8w', label: 'Every 8 weeks' }),
            new Option(undefined, { id: '2m', label: 'Every 2 months' }),
            new Option(undefined, { id: '9w', label: 'Every 9 weeks' }),
            new Option(undefined, { id: '10w', label: 'Every 10 weeks' }),
            new Option(undefined, { id: '11w', label: 'Every 11 weeks' }),
            new Option(undefined, { id: '12w', label: 'Every 12 weeks' }),
            new Option(undefined, { id: '3m', label: 'Every 3 months' }),
            new Option(undefined, { id: '4m', label: 'Every 4 months' }),
            new Option(undefined, { id: '6m', label: 'Every 6 months' }),
            new Option(undefined, { id: '12m', label: 'Every 12 months' }),
            new Option(undefined, { id: '1d', label: 'Every day' }),
            new Option(undefined, { id: '2d', label: 'Every 2 days' }),
            new Option(undefined, { id: '3d', label: 'Every 3 days' }),
            new Option(undefined, { id: '4d', label: 'Every 4 days' }),
            new Option(undefined, { id: '5d', label: 'Every 5 days' }),
        ]
    }

    static reminderStrings(): Option[] {
        return [
            new Option(undefined, { id: '', label: "Don't remind" }),
            new Option(undefined, { id: '24', label: '24 hours' }),
            new Option(undefined, { id: '48', label: '2 days' }),
            new Option(undefined, { id: '72', label: '3 days' }),
            new Option(undefined, { id: '120', label: '5 days' }),
            new Option(undefined, { id: '168', label: '7 days' }),
        ]
    }

    static sendReminderStrings(isCreate: boolean): Option[] {
        return [
            new Option(undefined, { id: '', label: 'Before every payment' }),
            new Option(undefined, {
                id: '1',
                label: isCreate ? 'Before first payment' : 'Before next payment',
            }),
        ]
    }

    static describe(frequency: string, interval: number): string {
        const keyString = `${interval}${frequency === 'daily' ? 'd' : frequency === 'weekly' ? 'w' : 'm'}`
        for (const i of Subscription.frequencyStrings()) {
            if (i.id === keyString) {
                return i.label!
            }
        }
        return ''
    }

    /**
     * Parses a frequency string into a frequency and interval
     * @param freqString - The frequency string to parse in the form of "1d", "2w", "3m", etc.
     * @returns The frequency and interval, e.g. "1w" -> { frequency: "weekly", interval: 1 }
     */
    static parseFrequencyString(
        freqString: string
    ): { frequency: string; interval: number } | undefined {
        const match = freqString.match(/^(\d+)([dwm])$/)
        if (!match) {
            return undefined
        }
        const interval = parseInt(match[1])
        const frequency = (() => {
            switch (match[2]) {
                case 'd':
                    return 'daily'
                case 'w':
                    return 'weekly'
                case 'm':
                    return 'monthly'
                default:
                    return undefined
            }
        })()

        if (!frequency) {
            return undefined
        }

        return { frequency, interval }
    }

    static instanceStrings(limit: number, minimum: number): Option[] {
        const appointmentStrings: Option[] = [
            new Option(undefined, { id: '', label: 'Continuous' }),
        ]
        for (let i = 1; i <= limit; i++) {
            if (i <= limit && i >= minimum) {
                appointmentStrings.push(
                    new Option(undefined, { id: i.toString(), label: i.toString() })
                )
            }
        }
        return appointmentStrings
    }

    jsonObject() {
        let dict = super.jsonObject()
        dict.plan = this.plan?.id
        dict.source = this.source?.id
        if (this.quantity !== undefined) dict.quantity = this.quantity
        dict.promotion = this.promotion?.id
        dict.memo = this.memo
        dict.cancellation_type = this.cancellationType
        dict.cancellation_memo = this.cancellationMemo
        dict.gift = this.gift?.id
        dict.deposit = this.deposit
        dict.discount = this.discount ?? 0
        dict.discount_type = this.discountType
        dict.gratuity = this.gratuity ?? 0
        dict.gratuity_type = this.gratuityType
        if (this.date) dict.date = this.date.format('YYYY-MM-DD')
        if (this.nextInvoiceDate && this.confirmedDate)
            dict.next_invoice_date = this.nextInvoiceDate.format('YYYY-MM-DD')
        dict.allow_surcharge = this.allowSurcharge
        dict.frequency = this.frequency
        dict.interval = this.interval
        dict.instances = this.instances
        dict.discount_instances = this.discountInstances
        dict.reminder_instances = this.reminderInstances
        dict.trial_period = this.trialPeriod
        return dict
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.plan) this.plan = BaseRecord.fromSerializer(data.plan, Item)
        if (data.source) this.source = BaseRecord.fromSerializer(data.source, Source)
        if (data.promotion) this.promotion = BaseRecord.fromSerializer(data.promotion, Promotion)
        if (data.gift) this.gift = BaseRecord.fromSerializer(data.gift, Gift)
        if (data.date) this.date = moment(data.date)
        if (data.confirmedDate) this.confirmedDate = moment(data.confirmedDate)
        if (data.completedDate) this.completedDate = moment(data.completedDate)
        if (data.paidDepositDate) this.paidDepositDate = moment(data.paidDepositDate)
        if (data.nextInvoiceDate) this.nextInvoiceDate = moment(data.nextInvoiceDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = mergeFk(Transaction.fk, {
        items: ['plan'],
        sources: ['source'],
        promotions: ['promotion'],
        gifts: ['gift'],
    })

    static dataType(): DataType {
        return 'subscriptions'
    }

    get plan(): Item | undefined {
        return this.getForeignRecord('plan', Item)
    }
    set plan(plan: Item | undefined) {
        this.setForeignRecord('plan', plan)
    }
    get source(): Source | undefined {
        return this.getForeignRecord('source', Source)
    }
    set source(source: Source | undefined) {
        this.setForeignRecord('source', source)
    }
    get gift(): Gift | undefined {
        return this.getForeignRecord('gift', Gift)
    }
    set gift(gift: Gift | undefined) {
        this.setForeignRecord('gift', gift)
    }
    get promotion(): Promotion | undefined {
        return this.getForeignRecord('promotion', Promotion)
    }
    set promotion(promotion: Promotion | undefined) {
        this.setForeignRecord('promotion', promotion)
    }
}

export const subscriptionPlanOptions = (
    subscription?: Subscription,
    edition?: Edition
): SelectOption[] => {
    if (
        subscription?.plan?.id &&
        [PREMIUM_ANNUAL_ID, PREMIUM_PLUS_ANNUAL_ID, PREMIUM_SUITE_ANNUAL_ID].includes(
            subscription.plan.id
        )
    ) {
        return [
            {
                value: 'premium',
                label: `Premium (${currencyFormat(subscription.plan.id === PREMIUM_ANNUAL_ID ? subscription.amount : PREMIUM_ANNUAL_PRICE)}/yr)`,
            },
            {
                value: 'plus',
                label: `Team (${currencyFormat(subscription.plan.id === PREMIUM_PLUS_ANNUAL_ID ? subscription.amount : PREMIUM_PLUS_ANNUAL_PRICE)}/yr)`,
            },
            {
                value: 'suite',
                label: `Enterprise (${currencyFormat(subscription.plan.id === PREMIUM_SUITE_ANNUAL_ID ? subscription.amount : PREMIUM_SUITE_ANNUAL_PRICE)}/yr)`,
            },
        ]
    }
    else {
        return [
            {
                value: 'premium',
                label: `Premium (${currencyFormat(subscription?.plan?.id === PREMIUM_ID ? subscription.amount : edition?.premiumPrice ? edition.premiumPrice : PREMIUM_PRICE)}/mo)`,
            },
            {
                value: 'plus',
                label: `Team (${currencyFormat(subscription?.plan?.id === PREMIUM_PLUS_ID ? subscription.amount : edition?.teamPrice ? edition.teamPrice : PREMIUM_PLUS_PRICE)}/mo)`,
            },
            {
                value: 'suite',
                label: `Enterprise (${currencyFormat(subscription?.plan?.id === PREMIUM_SUITE_ID ? subscription.amount : edition?.suitePrice ? edition.suitePrice : PREMIUM_SUITE_PRICE)}/mo)`,
            },
        ]
    }
}

export const subscriptionLabelString = (subscription: Subscription): string => {
    const name = subscription.plan?.name
    const nextInvoiceDate = subscription.nextInvoiceDate

    if (isPast(dateByMovingToEndOfDay(nextInvoiceDate)))
        return `Your subscription ${name ? 'to ' + name : ''} expired on ${dateFormat(nextInvoiceDate, SHORT_DATE_FORMAT)}.`

    if (subscription.trialPeriod && subscription.trialPeriod > 0) {
        const trialEndDate = subscription.createdDate?.add(subscription.trialPeriod, 'seconds')

        if (!isPast(trialEndDate))
            return `Your free trial ${name ? 'for ' + name : ''} expires on ${dateFormat(trialEndDate, SHORT_DATE_FORMAT)}.`
    }

    return `You are currently subscribed ${name ? 'to ' + name : ''} through ${dateFormat(nextInvoiceDate, SHORT_DATE_FORMAT)}.`
}
